import { useEffect, useMemo, useState } from "react";
import { Col, Form, Modal, Row } from "antd";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { FaDollarSign } from "react-icons/fa";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PageNotFound } from "../../../global/components/PageNotFound";
import MyPaymentForm from "../../../global/components/PaymentForm";
import useGet from "../../../global/hooks/useGet";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import { getDealerNo, hasRole } from "../../../global/hooks/getUserRole";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import PurchaseLicenseItemModal from "./PurchaseLicenseItemModal";
import { PurchaseLicenseItemsColumns } from "./PurchaseLicenseItemsColumns";

const ON_SITE_PURCHASE_TYPE = 0;

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `$${n.toFixed(2)}`;
};

const formatDiscount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0%";
  return `${n}%`;
};

export const PurchaseLicenseForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { siteNo, deviceNo, id } = useParams();
  const isDevicePage = deviceNo != null;
  const parentNo = isDevicePage ? deviceNo : siteNo;
  const parentPath = isDevicePage ? "device" : "site";
  const parentInfoPath = `/${parentPath}/info/${parentNo}`;
  const isSystemUser = hasRole(1, 2, 3);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);

  const parentUrl =
    parentNo != null && parentNo !== ""
      ? `${process.env.REACT_APP_API_URL}/api/${isDevicePage ? "devices" : "sites"}/${parentNo}/`
      : null;
  const {
    data: parentData,
    loading: parentLoading,
    error: parentError,
  } = useGet(parentUrl);

  const hasParentAccess = !parentError && Boolean(parentData?.data);

  const purchaseUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`
      : null;
  const itemsUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`
      : null;
  const deleteItemsUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/bulk-delete/`
      : null;
  const putUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`
      : null;

  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);
  const {
    data: deletedItemsData,
    error: deletedItemsError,
    deleteMultiple: deleteItemsData,
  } = useDelete(deleteItemsUrl);
  const { putData: putAction } = usePut(putUrl);

  const purchase = purchaseData?.data;
  const purchaseLoaded = Boolean(purchase) && !purchaseLoading;
  const dealerNo = purchase?.DealerNo ?? null;
  const hasPaymentTime =
    purchase?.PaymentTime != null && purchase?.PaymentTime !== "";
  const items = useMemo(
    () =>
      (itemsData?.data ?? []).map((item) => ({
        ...item,
        key: item.ItemNo,
      })),
    [itemsData],
  );

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  const optionUrl = purchaseLoaded
    ? `${process.env.REACT_APP_API_URL}/api/devices/options/`
    : null;
  const { data: optionsData, loading: optionDataLoading } = useGet(optionUrl);

  const resolveOemNo = (value) => {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const oemOption = optionsData?.data?.oems?.find(
      (oem) => String(oem?.name) === String(value),
    );
    return oemOption?.value ?? null;
  };

  const resolvedOemNo = resolveOemNo(purchase?.OemNo);

  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const { data: dealerOptions, loading: dealerOptionsLoading } =
    useGet(dealerOptionsUrl);

  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptions, null),
    [dealerOptions],
  );

  const renewalDealerNo = hasRole(7, 8, 9) ? getDealerNo() : dealerNo;
  const showRenewal = useMemo(() => {
    const dealer = (dealerOptions?.data ?? []).find(
      (d) => Number(d.value) === Number(renewalDealerNo),
    );
    return Number(dealer?.Renewal ?? dealer?.renewal) === 1;
  }, [dealerOptions, renewalDealerNo]);

  const discountPercent = Number(purchase?.Discount) || 0;
  const rawTotal = purchase?.Total;
  const totalForPayment = Number(rawTotal);
  const purchaseAmountProp =
    Number.isFinite(totalForPayment) && totalForPayment >= 0
      ? `$${totalForPayment.toFixed(2)}`
      : "$0.00";
  const showPayment =
    purchaseLoaded &&
    !hasPaymentTime &&
    rawTotal != null &&
    rawTotal !== "" &&
    Number.isFinite(totalForPayment) &&
    totalForPayment > 0;
  const canManageItems = purchaseLoaded && !hasPaymentTime;

  useEffect(() => {
    if (!purchaseLoaded) return;
    const apiSubTotal = Number(purchase?.SubTotal);
    const apiTotal = Number(purchase?.Total);
    form.setFieldsValue({
      OemName:
        purchase?.OemNo != null && purchase?.OemNo !== ""
          ? String(purchase.OemNo)
          : "",
      DealerNo: dealerNo != null && dealerNo !== "" ? Number(dealerNo) : null,
      SubTotal: Number.isFinite(apiSubTotal) ? formatMoney(apiSubTotal) : "",
      Discount: formatDiscount(discountPercent),
      Total: Number.isFinite(apiTotal) ? formatMoney(apiTotal) : "",
    });
  }, [form, purchaseLoaded, purchase, dealerNo, discountPercent]);

  useEffect(() => {
    if (deletedItemsError) {
      openErrorNotification(
        deletedItemsError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedItemsData) {
      openConfirmNotification("Success");
      setSelectedRowKeys([]);
      itemsRefetch();
      purchaseRefetch();
    }
  }, [deletedItemsData, deletedItemsError, itemsRefetch, purchaseRefetch]);

  if (
    !id ||
    !parentNo ||
    parentError ||
    (!parentLoading && !parentData?.data) ||
    purchaseError
  ) {
    return <PageNotFound />;
  }

  if (parentLoading || !purchaseLoaded) {
    return null;
  }

  const breadcrumbItems = [
    { label: isDevicePage ? "Device" : "Site", href: `/${parentPath}` },
    { label: "Information", href: parentInfoPath },
    {
      label: "Purchase License",
      href: `/${parentPath}/${parentNo}/purchase-license/${id}`,
    },
  ];
  const getPurchasePaidPayload = (paymentResponse) => {
    const amount = Number(
      String(purchaseAmountProp ?? "").replace(/[^0-9.-]+/g, ""),
    );
    const paymentNo =
      paymentResponse?.data?.PaymentNo ?? paymentResponse?.PaymentNo ?? null;
    const purchaseTime = purchase?.PurchaseTime
      ? dayjs(purchase.PurchaseTime).format("YYYY-MM-DD HH:mm:ss")
      : null;
    return {
      PurchaseTime: purchaseTime,
      PurchaseType: purchase?.PurchaseType ?? ON_SITE_PURCHASE_TYPE,
      OemNo: resolvedOemNo,
      DealerNo: dealerNo,
      Amount: Number.isFinite(amount) ? amount : 0,
      PaymentNo: paymentNo,
      DeviceNo: Number(parentNo),
    };
  };

  const openAddItemModal = () => {
    setEditingItem(null);
    setItemModalOpen(true);
  };

  const openEditItemModal = (record) => {
    if (!canManageItems || !record) return;
    setEditingItem(record);
    setItemModalOpen(true);
  };

  const onDeleteItemsConfirm = async () => {
    try {
      await deleteItemsData({ ids: selectedRowKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowItemsDeleteModal(false);
  };

  const handleItemsSaved = () => {
    setSelectedRowKeys([]);
    itemsRefetch();
    purchaseRefetch();
  };

  const buttonsData = [
    ...(showPayment
      ? [
          {
            label: "Payment",
            icon: <FaDollarSign />,
            onClick: () => setPaymentModal(true),
          },
        ]
      : []),
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: () => navigate(parentInfoPath),
      primary: !showPayment,
    },
  ];

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: openAddItemModal,
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowItemsDeleteModal(true),
      disabled: selectedRowKeys.length === 0,
    },
  ];

  return (
    <>
      <Form
        validateTrigger="onChange"
        form={form}
        scrollToFirstError
        layout="vertical"
        autoComplete="off"
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <PaymentIcon className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Purchase License
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="OEM"
                    name="OemName"
                    disabled
                    loading={optionDataLoading || purchaseLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={modifiedDealers}
                    disabled
                    placeholder="Please select"
                    loading={dealerOptionsLoading || purchaseLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="SubTotal" name="SubTotal" disabled />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Discount" name="Discount" disabled />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Total" name="Total" disabled />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <div className="flex justify-end mb-2">
        {canManageItems ? (
          <CommonButton buttonsData={itemButtonsData} />
        ) : (
          <div className="mt-2" />
        )}
      </div>
      <GlobalTable
        columns={PurchaseLicenseItemsColumns({ showRenewal })}
        dataSource={items}
        loading={itemsLoading}
        rowSelection={
          canManageItems
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
            : null
        }
        onRow={
          canManageItems
            ? (record) => ({
                onClick: (event) => {
                  const clickedSelectionControl =
                    event.target.closest(".ant-table-selection-column") ||
                    event.target.closest(".ant-checkbox-wrapper") ||
                    event.target.closest(".ant-checkbox-input");
                  if (clickedSelectionControl) return;
                  openEditItemModal(record);
                },
                style: { cursor: "pointer" },
              })
            : undefined
        }
      />

      <PurchaseLicenseItemModal
        open={itemModalOpen}
        setOpen={setItemModalOpen}
        purchaseId={id}
        deviceNo={parentNo}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        existingItems={items}
        showRenewal={showRenewal}
        onSaved={handleItemsSaved}
      />
      <ConfirmModal
        showModal={showItemsDeleteModal}
        setShowModal={setShowItemsDeleteModal}
        onDeleteConfirm={onDeleteItemsConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      <Modal
        title={
          <div className="flex items-center justify-between bg-gray-200 p-2.5 rounded-md">
            <div className="flex items-center">
              <PaymentIcon className="text-[18px] text-black" />
              <span className="text-[14px] font-[Poppins] text-black ml-2">
                Make Payment
              </span>
            </div>
          </div>
        }
        open={paymentModal}
        onCancel={() => {
          setPaymentModal(false);
          setShowCardLayout(!isSystemUser);
        }}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <MyPaymentForm
          DealerNo={dealerNo}
          amount={purchaseAmountProp}
          setPaymentModal={setPaymentModal}
          InvoiceNo={id}
          OrderName={
            purchase?.PurchaseTime ? String(purchase.PurchaseTime) : "Purchase"
          }
          refetch={purchaseRefetch}
          showCardLayout={showCardLayout}
          setShowCardLayout={setShowCardLayout}
          onPaymentSuccess={async (paymentResponse) => {
            const result = await putAction(
              getPurchasePaidPayload(paymentResponse),
            );
            if (result != null) {
              openConfirmNotification("Success");
              setPaymentModal(false);
              navigate(parentInfoPath);
            }
          }}
        />
      </Modal>
    </>
  );
};
